import { useGridApiRef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import { controlKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { generateFileName } from "../../../../../hooks/useFileNaming";
import WidgetWrapper from "../../WidgetWrapper";
import ControlsTableWidget from "./ControlsTableWidget";
import { filterTopOpenGaps } from "./utils/controlFilters";

const TopOpenGaps = () => {
  const { trackEvent, fetchAllControls } = useAPI();
  const gridRef = useGridApiRef();
  const navigate = useNavigate();
  const { data: controls, isLoading: loadingControls } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,
    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });

  const handleTableExport = () => {
    const message =
      "User click on 'Export' button to download the top open gaps controls table";
    trackEvent(message);
    const csvExportOptions = {
      fileName: generateFileName(`top-open-gaps-controls`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  const filteredControls = useMemo(() => {
    if (!controls) return [];
    return filterTopOpenGaps(controls);
  }, [controls]);

  const handleViewAll = () => {
    trackEvent("User clicked on 'View All' button in Top Open Gaps widget");
    navigate("/posture/controls", {
      state: {
        filters: {
          controlStatuses: {
            Gap: true,
            Compliant: false,
            Excluded: false,
            Snoozed: false,
          },
        },
        sort: [
          {
            field: "severity",
            sort: "desc",
          },
          {
            field: "affected_entities_count",
            sort: "desc",
          },
        ],
      },
    });
  };

  return (
    <WidgetWrapper
      title="Top Open Gaps"
      handleViewAll={handleViewAll}
      onExport={handleTableExport}
      info="Highest-severity gaps affecting the largest number of entities across your environment"
      viewAll={{
        handleViewAll: handleViewAll,
        tooltip: "Go to the full list view",
        newTab: false,
      }}
    >
      {loadingControls ? (
        <LoadingIcon size={12} />
      ) : (
        <ControlsTableWidget
          controls={filteredControls}
          loading={loadingControls}
          refToPass={gridRef}
          hideFooter={true}
          columnsToShow={[
            "id",
            "name_for_gap_state",
            "tool",
            "severity",
            "affected_entities_count",
          ]}
        />
      )}
    </WidgetWrapper>
  );
};

export default TopOpenGaps;
